import type { ComponentProps, ReactNode } from 'react'

import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip'
import { tv, type VariantProps } from 'tailwind-variants'

import { cn } from '../../../utils/cn'
import { Icon } from '../../atoms/icon/icon'
import { Text } from '../../atoms/text/text'

const popupVariants = tv({
  base: [
    'flex max-w-[228px] flex-col gap-2 rounded-sm p-4',
    'origin-[var(--transform-origin)]',
    'transition-[opacity,transform] duration-150 ease-out',
    'data-[starting-style]:opacity-0 motion-safe:data-[starting-style]:scale-95',
    'data-[ending-style]:opacity-0 motion-safe:data-[ending-style]:scale-95'
  ],
  variants: {
    variant: {
      dark: 'bg-primary',
      light:
        'bg-surface shadow-overlay outline-outline-strong outline outline-1'
    }
  },
  defaultVariants: { variant: 'dark' }
})

export type TooltipVariant = NonNullable<
  VariantProps<typeof popupVariants>['variant']
>

export type TooltipContentProps = TooltipPrimitive.Popup.Props &
  Pick<
    TooltipPrimitive.Positioner.Props,
    'align' | 'alignOffset' | 'side' | 'sideOffset'
  > & {
    variant?: TooltipVariant
    /** Override the portal layer when the trigger lives in a modal surface. */
    positionerClassName?: string
    title?: string
    onClose?: () => void
    children: ReactNode
  }

function TooltipProvider({
  delay = 0,
  ...props
}: TooltipPrimitive.Provider.Props) {
  return (
    <TooltipPrimitive.Provider
      data-slot='tooltip-provider'
      delay={delay}
      {...props}
    />
  )
}

function Tooltip({ ...props }: TooltipPrimitive.Root.Props) {
  return <TooltipPrimitive.Root data-slot='tooltip' {...props} />
}

function TooltipTrigger({ ...props }: TooltipPrimitive.Trigger.Props) {
  return <TooltipPrimitive.Trigger data-slot='tooltip-trigger' {...props} />
}

function TooltipContent({
  className,
  positionerClassName,
  variant = 'dark',
  title,
  onClose,
  side = 'bottom',
  sideOffset = 8,
  align = 'start',
  alignOffset = 2,
  children,
  ...props
}: TooltipContentProps) {
  const isDark = variant === 'dark'

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        className={cn('isolate z-50', positionerClassName)}
      >
        <TooltipPrimitive.Popup
          data-slot='tooltip'
          data-variant={variant}
          className={cn(popupVariants({ variant }), className)}
          {...props}
        >
          {title && (
            <div className='flex items-center gap-1'>
              <Text
                as='span'
                variant='body-md'
                weight='semibold'
                truncate
                className={cn(
                  'min-w-0 flex-1 leading-normal',
                  isDark ? 'text-on-primary' : 'text-surface-title-50'
                )}
              >
                {title}
              </Text>
              {onClose && (
                <button
                  type='button'
                  onClick={onClose}
                  className={cn(
                    'shrink-0 cursor-pointer',
                    isDark ? 'text-on-primary' : 'text-surface-title-50'
                  )}
                  aria-label='Close tooltip'
                >
                  <Icon name='close' className='text-[16px]' />
                </button>
              )}
            </div>
          )}
          <Text
            as='div'
            variant='caption-lg'
            className={cn(
              'leading-normal',
              isDark ? 'text-surface-300' : 'text-surface-body'
            )}
          >
            {children}
          </Text>
          <TooltipPrimitive.Arrow
            className={cn(
              'flex',
              'data-[side=bottom]:-top-2 data-[side=bottom]:rotate-0',
              'data-[side=top]:-bottom-2 data-[side=top]:rotate-180',
              'data-[side=left]:right-[-13px] data-[side=left]:rotate-90',
              'data-[side=right]:left-[-13px] data-[side=right]:-rotate-90'
            )}
          >
            <ArrowSvg
              className={
                isDark
                  ? '[&_.arrow-fill]:fill-primary'
                  : '[&_.arrow-fill]:fill-surface [&_.arrow-stroke]:fill-outline-strong'
              }
            />
          </TooltipPrimitive.Arrow>
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  )
}

function ArrowSvg(props: ComponentProps<'svg'>) {
  return (
    <svg width='20' height='10' viewBox='0 0 20 10' fill='none' {...props}>
      <path
        d='M9.66437 2.60207L4.80758 6.97318C4.07308 7.63423 3.11989 8 2.13172 8H0V10H20V8H18.5349C17.5468 8 16.5936 7.63423 15.8591 6.97318L11.0023 2.60207C10.622 2.2598 10.0447 2.25979 9.66437 2.60207Z'
        className='arrow-fill'
      />
      <path
        d='M8.99542 1.85876C9.75604 1.17425 10.9106 1.17422 11.6713 1.85878L16.5281 6.22989C17.0789 6.72568 17.7938 7.00001 18.5349 7.00001L15.89 7L11.0023 2.60207C10.622 2.2598 10.0447 2.2598 9.66436 2.60207L4.77734 7L2.13171 7.00001C2.87284 7.00001 3.58774 6.72568 4.13861 6.22989L8.99542 1.85876Z'
        className='arrow-stroke'
      />
    </svg>
  )
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger }
